/**
 * Editor & Puzzle JSON Type Definitions
 *
 * PuzzleJSON is the structured format stored in Supabase puzzle_json column.
 * TextFitResult describes how text should render in a constrained region.
 */

/** Display hints for manual text fitting overrides (line breaks, hyphens) */
export interface DisplayHints {
  lineBreaks?: number[];
  hyphens?: number[];
}

/** Structured puzzle format stored as JSONB in Supabase */
export interface PuzzleJSON {
  solution: {
    primary: string;
    alternates: string[];
  };
  columns: Array<{
    category: string;
    pairs: Array<{
      clues: [string, string]; // [top, bottom]
      hiddenIndex: 0 | 1;
      joiner: string | null;
    }>;
    answerAlternates: string[]; // alternates for column answer (pair 2's hidden clue)
  }>;
  displayHints?: {
    clues?: Record<string, DisplayHints>;  // keyed by "col-row"
    recap?: Record<string, DisplayHints>;  // keyed by "col-rect"
  };
}

/** Text fitting result for a single text region */
export interface TextFitResult {
  lines: string[];
  fontSize: number;
  isSmaller: boolean;
  needsManualFix: boolean;
  /**
   * For recap pair rects: indicates which line the joiner text was placed on.
   * 1 = appended to line 1, 2 = prepended to line 2, 'split' = split across both.
   * Undefined for non-pair fits or when there is no joiner.
   */
  joinerOnLine?: 1 | 2 | 'split';
  /**
   * For recap pair rects: index of the last line of the first half.
   * The renderer should insert a larger gap after this line to visually
   * separate the top-clue group from the bottom-clue group.
   */
  halfBreakAfterLine?: number;
  /**
   * For recap answer rects: the font size for the context (non-answer) line.
   * Sized independently from the answer — context may shrink while answer stays large.
   */
  contextFontSize?: number;
}

/** Creates a blank PuzzleJSON with 4 columns, 3 pairs each */
export function createBlankPuzzle(): PuzzleJSON {
  return {
    solution: { primary: '', alternates: [] },
    columns: Array.from({ length: 4 }, () => ({
      category: '',
      pairs: Array.from({ length: 3 }, () => ({
        clues: ['', ''] as [string, string],
        hiddenIndex: 1 as const,
        joiner: null,
      })),
      answerAlternates: [],
    })),
  };
}
